import { APPConfig } from 'config/appConfig'

/**
 * Mirrors account-profile-mfe/nu-jitsu's addressFieldsUtils so the same
 * `citizenshipFields` / `citizenshipFieldsApplicableUserType` config keys
 * (set per-market via the same config source) work identically across all
 * three apps.
 */

const FIELD_KEY_ALIASES = {
  placeofbirth: 'placeOfBirth',
  countryofbirth: 'countryOfBirth',
  nationality: 'nationality',
  citizenship: 'nationality',
}

export function parseConfigStringArray(value) {
  if (!value) {
    return []
  }
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean)
  }
  const trimmed = String(value).trim()
  if (!trimmed) {
    return []
  }
  try {
    const parsed = JSON.parse(trimmed)
    if (Array.isArray(parsed)) {
      return parsed.map(String).filter(Boolean)
    }
  } catch (e) {
    // fall through to comma-separated parsing
  }
  return trimmed
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

export function normalizeAddressFieldKey(value) {
  const normalized = value?.replace(/\s+/g, '')?.toLowerCase() || ''
  return FIELD_KEY_ALIASES[normalized] || null
}

export function normalizeAddressFieldKeys(rawValues) {
  const seen = new Set()
  const fields = []
  rawValues?.forEach(value => {
    const fieldKey = normalizeAddressFieldKey(value)
    if (fieldKey && !seen.has(fieldKey)) {
      seen.add(fieldKey)
      fields.push(fieldKey)
    }
  })
  return fields
}

export function normalizeUserTypeForAddressFields(userType) {
  if (!userType) {
    return ''
  }
  return userType.replace(/\s+/g, '_').replaceAll('-', '_').toUpperCase()
}

export function getEffectiveUserTypesForAddressFields(userType) {
  const effectiveTypes = new Set()
  const normalizedUserType = normalizeUserTypeForAddressFields(userType)

  if (normalizedUserType) {
    effectiveTypes.add(normalizedUserType)
  }
  if (normalizedUserType === 'BRAND_AFFILIATE' || userType === 'Brand Affiliate') {
    effectiveTypes.add('BRAND_AFFILIATE')
  }
  if (normalizedUserType === 'BUSINESS_ENTITY') {
    effectiveTypes.add('BUSINESS_ENTITY')
  }
  return effectiveTypes
}

export function getConfiguredAddressFieldRows(fields) {
  const has = fieldKey => fields?.includes(fieldKey)
  const rows = []

  if (has('placeOfBirth')) {
    rows.push(['placeOfBirth'])
  }

  const countryAndNationalityRow = []
  if (has('countryOfBirth')) countryAndNationalityRow.push('countryOfBirth')
  if (has('nationality')) countryAndNationalityRow.push('nationality')
  if (countryAndNationalityRow.length > 0) rows.push(countryAndNationalityRow)

  return rows
}

export function getAddressFieldsConfig() {
  const { citizenshipFields, citizenshipFieldsApplicableUserType } =
    APPConfig?.getAppConfig() || {}
  const fields = normalizeAddressFieldKeys(parseConfigStringArray(citizenshipFields))
  const userTypes = parseConfigStringArray(citizenshipFieldsApplicableUserType).map(
    normalizeUserTypeForAddressFields
  )
  return { fields, userTypes }
}

export function isUserTypeEligibleForAddressFields(userType) {
  const { userTypes } = getAddressFieldsConfig()
  if (userTypes.length === 0) {
    return false
  }
  const effectiveTypes = getEffectiveUserTypesForAddressFields(userType)
  return userTypes.some(configuredType => effectiveTypes.has(configuredType))
}

export function shouldShowCitizenshipFields(options = {}) {
  const { fields, userTypes } = getAddressFieldsConfig()
  if (fields.length === 0 || userTypes.length === 0) {
    return false
  }
  return isUserTypeEligibleForAddressFields(options.userType)
}
